/**
 * Shared contrast-aware color resolver for team-colored chart elements
 * (PMA-COCK-180). A team's raw `primary`/`secondary` color can be black,
 * near-black, or otherwise too dark to register against the near-black
 * analytics panel background — this module decides what to actually draw
 * on screen so a "black and white" team never disappears.
 *
 * Originally private to `momentum/MatchMomentumChart.tsx` (hex/HSL math +
 * `brightenForContrast`); moved here so `BallRecoveryTimeBanner.tsx` (and
 * any future chart) can share the exact same rule instead of re-deriving
 * its own floor logic (the recovery banner previously had none at all —
 * the bug this UC fixes).
 */
import { relativeLuminance } from "../../../lib/teamPalette";

/** Chart surface a resolved color will be used for — each has its own
 * legibility floor (see the per-constant docs below). */
export type ChartColorRole = "fill" | "stroke" | "text";

export interface ChartTeamColorInput {
  primary: string;
  secondary?: string;
}

// Minimum WCAG relative luminance a color must clear before it's used as a
// solid chart fill (area gradients, accent bar, legend swatches, chip
// backgrounds) against the near-black panel background.
export const FILL_MIN_LUMINANCE = 0.2;
// Strokes/hairlines (chart area outlines, goal reference lines, sparkline
// lines) need a higher floor — a 1-2px line has far less area to register
// than a filled region.
export const STROKE_MIN_LUMINANCE = 0.45;
// Text (recovery-banner seconds, "Fastest" chip label) needs the same
// higher floor as a stroke — small glyph strokes have the same legibility
// problem as a hairline.
export const TEXT_MIN_LUMINANCE = 0.45;
// A color at or below this HSL saturation is treated as "achromatic" (pure
// black/white/gray) — it has no hue to preserve, so brightening it would
// only produce a washed-out gray, not a recognizable team color.
export const ACHROMATIC_SATURATION_MAX = 0.12;
// Fixed light neutral used when a team's primary is achromatic and has no
// usable chromatic secondary to fall back on — reads as "the white/black
// team" instead of vanishing into the panel background.
export const LIGHT_NEUTRAL = "#e5e7eb";

// Shared momentum-chart defaults (PMA-COCK-058) — also `resolveChartTeamPair`'s
// last-resort away fallback when two resolved colors are indistinguishable.
export const DEFAULT_HOME_COLOR = "#10b981";
export const DEFAULT_AWAY_COLOR = "#3b82f6";

const ROLE_MIN_LUMINANCE: Record<ChartColorRole, number> = {
  fill: FILL_MIN_LUMINANCE,
  stroke: STROKE_MIN_LUMINANCE,
  text: TEXT_MIN_LUMINANCE,
};

// Two resolved colors count as "not distinguishable" when they're both
// close in lightness AND (close in hue OR both achromatic gray/near-gray).
const PAIR_HUE_DISTANCE_THRESHOLD_DEG = 20;
const PAIR_LIGHTNESS_DISTANCE_THRESHOLD = 0.15;

export function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#([0-9A-Fa-f]{6})$/.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

export function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (v: number) =>
    Math.round(Math.min(255, Math.max(0, v)))
      .toString(16)
      .padStart(2, "0");
  return `#${clamp(r)}${clamp(g)}${clamp(b)}`;
}

export function rgbToHsl(
  r: number,
  g: number,
  b: number,
): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return [h, s, l];
}

function hueToRgbChannel(p: number, q: number, t: number): number {
  let tt = t;
  if (tt < 0) tt += 1;
  if (tt > 1) tt -= 1;
  if (tt < 1 / 6) return p + (q - p) * 6 * tt;
  if (tt < 1 / 2) return q;
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
  return p;
}

export function hslToRgb(
  h: number,
  s: number,
  l: number,
): [number, number, number] {
  if (s === 0) return [l * 255, l * 255, l * 255];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    hueToRgbChannel(p, q, h + 1 / 3) * 255,
    hueToRgbChannel(p, q, h) * 255,
    hueToRgbChannel(p, q, h - 1 / 3) * 255,
  ];
}

/**
 * Raise a color's lightness (hue/saturation held constant, so it stays
 * vivid rather than washing out toward gray) until it clears `minLuminance`.
 * Colors already above the threshold pass through unchanged.
 */
export function brightenForContrast(hex: string, minLuminance: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const [h, s, startL] = rgbToHsl(...rgb);
  let l = startL;
  let current = hex;
  let steps = 0;
  while (
    (relativeLuminance(current) ?? 0) < minLuminance &&
    l < 0.95 &&
    steps < 40
  ) {
    l += 0.03;
    current = rgbToHex(...hslToRgb(h, s, l));
    steps += 1;
  }
  return current;
}

/**
 * Resolve the display color for one team-colored chart element.
 *
 * Order:
 * 1. `primary` as-is, if its relative luminance already clears the role's
 *    floor (colored teams that already look fine render exactly as today).
 * 2. `secondary`, brightened up to the floor if needed, when it has real
 *    chroma (saturation above `ACHROMATIC_SATURATION_MAX`) — a chromatic
 *    secondary is always preferable to falling through to a flat neutral,
 *    even if the raw secondary hex itself doesn't yet clear the floor (e.g.
 *    a dark accent red still reads as "this team's red" once brightened).
 * 3. `primary`, brightened up to the floor, when `primary` itself has real
 *    chroma (e.g. a dark navy stays navy, just lighter).
 * 4. `LIGHT_NEUTRAL` when `primary` is achromatic (black/near-black/gray)
 *    and no usable chromatic secondary exists — brightening a gray would
 *    only produce a different gray, not a recognizable team color.
 *
 * Unparseable `primary` input (not a `#rrggbb` hex) passes through
 * unchanged — this function never throws on bad data.
 */
export function resolveChartColor(
  primary: string,
  secondary: string | undefined,
  role: ChartColorRole,
): string {
  const floor = ROLE_MIN_LUMINANCE[role];
  const primaryRgb = hexToRgb(primary);
  if (!primaryRgb) return primary;

  const primaryLuminance = relativeLuminance(primary);
  if (primaryLuminance !== null && primaryLuminance >= floor) {
    return primary;
  }

  if (secondary) {
    const secondaryRgb = hexToRgb(secondary);
    if (secondaryRgb) {
      const [, secondarySaturation] = rgbToHsl(...secondaryRgb);
      if (secondarySaturation > ACHROMATIC_SATURATION_MAX) {
        return brightenForContrast(secondary, floor);
      }
    }
  }

  const [, primarySaturation] = rgbToHsl(...primaryRgb);
  if (primarySaturation <= ACHROMATIC_SATURATION_MAX) {
    return LIGHT_NEUTRAL;
  }

  return brightenForContrast(primary, floor);
}

/** True when two resolved colors would read as visually indistinguishable
 * side by side — close in lightness, and either close in hue or both
 * achromatic grays. */
function areColorsTooClose(a: string, b: string): boolean {
  const rgbA = hexToRgb(a);
  const rgbB = hexToRgb(b);
  if (!rgbA || !rgbB) return false;

  const [hueA, satA, lightA] = rgbToHsl(...rgbA);
  const [hueB, satB, lightB] = rgbToHsl(...rgbB);

  const lightnessDistance = Math.abs(lightA - lightB);
  if (lightnessDistance >= PAIR_LIGHTNESS_DISTANCE_THRESHOLD) return false;

  const bothAchromatic =
    satA <= ACHROMATIC_SATURATION_MAX && satB <= ACHROMATIC_SATURATION_MAX;
  if (bothAchromatic) return true;

  const hueDeltaDeg = Math.abs(hueA - hueB) * 360;
  const hueDistanceDeg = Math.min(hueDeltaDeg, 360 - hueDeltaDeg);
  return hueDistanceDeg < PAIR_HUE_DISTANCE_THRESHOLD_DEG;
}

/**
 * Resolve both sides of a home/away pair, then guarantee they stay
 * distinguishable from each other. If the two independently-resolved
 * colors are too close (see `areColorsTooClose`), the away side is
 * re-resolved from its own secondary color (if it has one); if that is
 * still too close — or there was no secondary to try — away falls back to
 * `DEFAULT_AWAY_COLOR`. `home` is never adjusted.
 */
export function resolveChartTeamPair(
  home: ChartTeamColorInput,
  away: ChartTeamColorInput,
  role: ChartColorRole,
): { home: string; away: string } {
  const homeColor = resolveChartColor(home.primary, home.secondary, role);
  let awayColor = resolveChartColor(away.primary, away.secondary, role);

  if (areColorsTooClose(homeColor, awayColor)) {
    awayColor = away.secondary
      ? resolveChartColor(away.secondary, undefined, role)
      : DEFAULT_AWAY_COLOR;

    if (areColorsTooClose(homeColor, awayColor)) {
      awayColor = DEFAULT_AWAY_COLOR;
    }
  }

  return { home: homeColor, away: awayColor };
}
